/**
 * nav — モバイル用ハンバーガー / ドロワー
 *
 * - 見た目は CSS（.l-nav-drawer / .is-open）。JS は状態クラス + a11y 属性のみ。
 * - aria-expanded / aria-controls、Escape で閉じる、外側（バックドロップ）タップで閉じる、
 *   開いている間はフォーカスをドロワー内に閉じ込める、閉じたらトグルへフォーカスを戻す、
 *   背面スクロールをロック（<html>/<body> に .has-nav-open）。
 * - prefers-reduced-motion のときはトランジション待ちをしない。
 * - ハンバーガーは <=768px でのみ表示（CSS）。JS 無効時は表示されない。
 */

const OPEN_CLASS = "is-open";
const LOCK_CLASS = "has-nav-open";
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function initNav(root = document) {
  const toggle = root.querySelector(".js-sp-nav-toggle");
  const drawer = root.querySelector(".js-sp-nav-drawer");
  if (!toggle || !drawer) {
    return;
  }
  const panel = drawer.querySelector(".l-nav-drawer__panel");
  if (!panel) {
    return;
  }

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let lastFocused = null;
  let closeTimer = 0;

  function visibleFocusables() {
    return Array.from(drawer.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  }

  function onKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== "Tab") {
      return;
    }
    const items = visibleFocusables();
    if (items.length === 0) {
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function open() {
    if (drawer.classList.contains(OPEN_CLASS)) {
      return;
    }
    window.clearTimeout(closeTimer);
    lastFocused = document.activeElement;
    drawer.hidden = false;
    // 強制リフロー: hidden 解除直後に .is-open を付けてトランジションを走らせる。
    void drawer.offsetWidth;
    drawer.classList.add(OPEN_CLASS);
    document.documentElement.classList.add(LOCK_CLASS);
    document.body.classList.add(LOCK_CLASS);
    toggle.setAttribute("aria-expanded", "true");
    const focusTarget = panel.querySelector(FOCUSABLE) || panel;
    focusTarget.focus({ preventScroll: true });
    document.addEventListener("keydown", onKeydown);
  }

  function close() {
    if (!drawer.classList.contains(OPEN_CLASS)) {
      return;
    }
    drawer.classList.remove(OPEN_CLASS);
    document.documentElement.classList.remove(LOCK_CLASS);
    document.body.classList.remove(LOCK_CLASS);
    toggle.setAttribute("aria-expanded", "false");
    document.removeEventListener("keydown", onKeydown);

    const finish = () => {
      drawer.hidden = true;
      panel.removeEventListener("transitionend", finish);
    };
    if (reduceMotion) {
      finish();
    } else {
      panel.addEventListener("transitionend", finish);
      closeTimer = window.setTimeout(finish, 420);
    }

    if (lastFocused && typeof lastFocused.focus === "function") {
      lastFocused.focus({ preventScroll: true });
    }
  }

  toggle.addEventListener("click", () => {
    if (drawer.classList.contains(OPEN_CLASS)) {
      close();
    } else {
      open();
    }
  });

  drawer.querySelectorAll(".js-sp-nav-close").forEach((el) => {
    el.addEventListener("click", close);
  });

  // ドロワー内リンクをタップしたら閉じる（同一ページ内アンカー等でも破綻しない）。
  panel.querySelectorAll("a[href]").forEach((link) => {
    link.addEventListener("click", () => {
      close();
    });
  });

  // 幅が PC に戻ったら開いたままにしない。
  window.matchMedia("(min-width: 769px)").addEventListener("change", (event) => {
    if (event.matches) {
      close();
    }
  });
}
